import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight } from "lucide-react";
import { useT } from "@/context/I18nContext";

export function BlogList() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/recipes").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="blog-list">
      <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brunchi-green-hover)] mb-3">{t("blog.eyebrow")}</div>
      <h1 className="font-display text-4xl lg:text-5xl tracking-tight">{t("blog.title")}</h1>
      <p className="mt-3 text-[color:var(--brunchi-ink-soft)] max-w-xl">{t("blog.subtitle")}</p>
      {items.length === 0 ? (
        <div className="mt-16 text-center text-[color:var(--brunchi-ink-soft)]" data-testid="blog-empty">{t("blog.coming")}</div>
      ) : (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((r) => (
            <Link key={r.id} to={`/blog/${r.slug}`}
              className="group bg-white rounded-2xl overflow-hidden border border-[color:var(--brunchi-stone)] hover:shadow-lg transition"
              data-testid={`recipe-card-${r.slug}`}>
              {r.hero_image && (
                <div className="aspect-[16/10] overflow-hidden bg-[color:var(--brunchi-cream)]">
                  <img src={r.hero_image.startsWith("/api/media") ? `${r.hero_image}&w=640` : r.hero_image}
                       alt={r.title} loading="lazy" decoding="async"
                       className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                </div>
              )}
              <div className="p-5">
                <div className="flex gap-1.5 mb-2">
                  {(r.tags || []).slice(0, 2).map((tag) => (
                    <span key={tag} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[color:var(--brunchi-cream-2)] text-[color:var(--brunchi-forest)]">{tag}</span>
                  ))}
                </div>
                <div className="font-display text-lg font-semibold leading-tight">{r.title}</div>
                <div className="mt-1 text-sm text-[color:var(--brunchi-ink-soft)] line-clamp-2">{r.excerpt}</div>
                <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[color:var(--brunchi-green-hover)]">
                  {t("blog.read")} <ArrowRight size={12} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function BlogDetail() {
  const { slug } = useParams();
  const { t } = useT();
  const [r, setR] = useState(null);
  useEffect(() => { api.get(`/recipes/${slug}`).then((res) => setR(res.data)).catch(() => setR(false)); }, [slug]);
  if (r === null) return <div className="text-center py-24 text-[color:var(--brunchi-ink-soft)]">{t("blog.loading")}</div>;
  if (r === false) return (
    <div className="text-center py-24" data-testid="blog-not-found">
      <div className="font-display text-2xl">{t("blog.not_found")}</div>
      <Link to="/blog" className="text-[color:var(--brunchi-green-hover)] underline text-sm">{t("blog.back_all")}</Link>
    </div>
  );
  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="blog-detail">
      <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brunchi-green-hover)] mb-3">{r.tags?.join(" · ") || t("blog.default_tag")}</div>
      <h1 className="font-display text-4xl lg:text-5xl tracking-tight leading-[1.1]">{r.title}</h1>
      {r.excerpt && <p className="mt-3 text-lg text-[color:var(--brunchi-ink-soft)]">{r.excerpt}</p>}
      {r.hero_image && <img src={r.hero_image} alt={r.title} className="mt-6 w-full aspect-[16/9] object-cover rounded-2xl" />}
      <div className="mt-6 text-[color:var(--brunchi-ink)] leading-relaxed whitespace-pre-wrap text-base">
        {r.body_md}
      </div>
    </article>
  );
}
